import { LIBRARY_OBJECTS, libraryAssetHasGrasps } from "./libraryCatalog";
import { getGraspActions } from "../stores/graspStore";
import { getSceneState } from "../stores/sceneStore";

type LibraryObjectEntry = {
  folder?: string;
  id?: string;
  source?: string;
};

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Map an MJCF body name to a library `objects/<id>/` folder so we can load
 * grasps.json for objects that entered the scene via merged XML (e.g. iTHOR
 * floorplan), not only via {@link loadLibraryAsset}.
 *
 * Molmo roots look like `Fridge_1`, `Desk_310_1` (not `desk_cabinet_*`).
 * Child bodies must not match.
 */
export function resolveLibraryGraspFolderForRootBodyName(
  rootName: string | null | undefined
): string | null {
  if (!rootName || typeof rootName !== "string") return null;
  const t = rootName.trim();
  const lower = t.toLowerCase();

  if (/^Fridge_\d+$/i.test(t)) return "fridge";
  if (/^Desk_\d+_\d+$/i.test(t)) return "desk_cabinet";

  for (const asset of LIBRARY_OBJECTS) {
    if (asset.folder !== "objects") continue;
    const id = asset.id;
    if (lower === id) return id;
    const re = new RegExp(`^${escapeRegExp(id)}_\\d+$`, "i");
    if (re.test(t)) return id;
  }
  return null;
}

/**
 * For every body in the scene whose MJCF name maps to a library object folder,
 * fetch grasps into {@link useGraspStore} under that body's UUID.
 *
 * Uses all bodies, not only {@link SceneStoreState.worldBodies}, so nested
 * includes (floorplan roots) still get grasps.
 */
export async function syncLibraryGraspsFromSceneRootBodies(): Promise<void> {
  const { bodies } = getSceneState();
  if (!bodies || typeof bodies !== "object") return;

  const graspActions = getGraspActions();
  const loads: Promise<void>[] = [];

  for (const [bodyUuid, body] of Object.entries(bodies)) {
    if (!body || typeof bodyUuid !== "string") continue;
    const folder = resolveLibraryGraspFolderForRootBodyName(body.name);
    if (folder) {
      const asset = LIBRARY_OBJECTS.find(
        (entry: LibraryObjectEntry) => entry.folder === "objects" && entry.id === folder
      );
      if (libraryAssetHasGrasps(asset)) {
        loads.push(graspActions.loadGraspsForBody(bodyUuid, folder, { source: asset?.source }));
      }
    }
  }

  await Promise.all(loads);
}
